import { createApi } from '@reduxjs/toolkit/query/react';
import { Endpoint } from 'services/endpoints';

import { HTTPMethod, Response } from 'types/http';

import { getDefaultPagination } from 'utils/pagination';

import API_CONFIG from '../../constants/api';
import { auth0TokenStorage, fetchBaseQueryWithAuth } from '../api';
import { CustomHeaderType } from '../api/types';
import { TAGS } from './keys';
import { CryptoPayoutCreateResponse, CryptoPayoutsListQuery, CryptoPayoutsListResponse } from './types';

const REDUCER_PATH = 'cryptoPayoutsApi';

export const cryptoPayoutsApi = createApi({
  reducerPath: REDUCER_PATH,
  baseQuery: fetchBaseQueryWithAuth,
  tagTypes: [TAGS.CRYPTO_PAYOUTS_LIST],
  endpoints: (builder) => ({
    getCryptoPayout: builder.query<CryptoPayoutCreateResponse, { uuid: string }>({
      query: ({ uuid }) => ({
        url: Endpoint.CryptoPayouts.ById(uuid),
        method: HTTPMethod.GET,
      }),
    }),
    cancelCryptoPayout: builder.mutation<{ success: boolean }, { uuid: string }>({
      query: ({ uuid }) => ({
        url: Endpoint.CryptoPayouts.CancelById(uuid),
        method: HTTPMethod.POST,
      }),
    }),
    getPayoutReceipt: builder.query<Blob, string>({
      query: (payoutUuid) => ({
        url: Endpoint.CryptoPayouts.Receipt(payoutUuid),
        method: HTTPMethod.GET,
        responseHandler: (response) => response.blob(),
      }),
    }),
    getCryptoPayoutsList: builder.query<CryptoPayoutsListResponse, CryptoPayoutsListQuery>({
      query: (requestBody) => ({
        url: Endpoint.CryptoPayouts.List,
        method: HTTPMethod.POST,
        body: requestBody,
      }),
      transformResponse: (response: Response<CryptoPayoutsListResponse>) => {
        if (!response?.data) {
          return { data: [], pagination: getDefaultPagination(), requestId: response.requestId };
        }

        return response.data;
      },
      providesTags: [TAGS.CRYPTO_PAYOUTS_LIST],
    }),
  }),
});

export const getCSVRecords = async (input: object) => {
  const URL = [API_CONFIG.ROOT_URL, Endpoint.CryptoPayouts.CSV].join('');
  const headers = new Headers();
  headers.set('Content-type', 'application/json');
  const accessToken = await auth0TokenStorage.getAccessToken()();
  if (accessToken) {
    headers.set(CustomHeaderType.Auth, `Bearer ${accessToken}`);
  }
  const response = await fetch(URL, {
    method: HTTPMethod.POST,
    headers,
    body: JSON.stringify(input),
  });
  const blob = await response.blob();
  return blob;
};

export const { useGetCryptoPayoutQuery, useCancelCryptoPayoutMutation, useLazyGetPayoutReceiptQuery, useGetCryptoPayoutsListQuery } =
  cryptoPayoutsApi;
export default cryptoPayoutsApi;
